import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tag } from "@/components/Layout";
import { Check, Loader2, Save, Send, FileSignature, LogOut, Shield } from "lucide-react";
import { toast } from "sonner";
import SignaturePad from "@/components/SignaturePad";
import { formatDateTime } from "@/lib/dateFormat";

const STATUS_TONE = { invited: "blue", changes_requested: "orange", submitted: "yellow", active: "green", suspended: "red" };
const REQUIRED_FIELDS = ["company", "gstin", "address", "payment_terms"];

export default function VendorOnboarding() {
  const { logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [sigOpen, setSigOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/onboarding/me");
      setMe(data); setForm(data);
      if (data.status === "active") nav("/vendor", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const editable = me && (me.status === "invited" || me.status === "changes_requested");

  const save = useCallback(async (silent = false) => {
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id; delete payload.email; delete payload.kind; delete payload.status;
      delete payload.submitted_at; delete payload.approved_at; delete payload.nda_signature_url; delete payload.nda_signed_at;
      const { data } = await api.patch("/vendor/onboarding/profile", payload);
      setMe(data); setForm(data);
      if (!silent) toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }, [form]);

  const onSign = async (sig) => {
    setSigning(true);
    try {
      const signatureDataUrl = sig.signature_kind === "drawn" ? sig.signature_image : `data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 80'><text x='10' y='55' font-family='cursive' font-size='44'>${encodeURIComponent(sig.signature_image)}</text></svg>`;
      const { data } = await api.post("/vendor/onboarding/nda/sign", { signature_data_url: signatureDataUrl, signer_name: sig.signer_name });
      toast.success("NDA signed");
      setMe((m) => ({ ...m, nda_signed_at: data.signed_at }));
      setSigOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSigning(false); }
  };

  const submit = async () => {
    await save(true);
    try {
      await api.post("/vendor/onboarding/submit");
      toast.success("Submitted. Studio admin will review shortly.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!me) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>;

  const missing = REQUIRED_FIELDS.filter((k) => !form[k]);
  const submittedView = me.status === "submitted";

  return (
    <div className="min-h-screen bg-slate-50" data-testid="vendor-onboarding-page">
      <header className="bg-white border-b border-slate-200 px-4 sm:px-8 py-3 flex items-center gap-3">
        <Shield size={18} className="text-[#F54800]" />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Vendor onboarding</div>
          <div className="font-display font-bold text-sm">{me.company || me.name}</div>
        </div>
        <Tag tone={STATUS_TONE[me.status] || "slate"} data-testid="vendor-status-tag">{(me.status || "").replace("_", " ")}</Tag>
        <button onClick={() => { logout().then(() => nav("/login")); }} className="text-[11px] text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="vendor-logout"><LogOut size={11} /> Sign out</button>
      </header>

      <div className="max-w-3xl mx-auto p-4 sm:p-8 space-y-4">
        {submittedView && (
          <div className="card-flat p-5 border-l-4 border-l-yellow-500 bg-yellow-50/50" data-testid="vendor-pending-banner">
            <div className="font-display text-base text-slate-900">Submitted for review</div>
            <p className="text-xs text-slate-600 mt-1">Your details are in the studio&apos;s approval queue. You&apos;ll get an email the moment they approve you. You won&apos;t be able to edit anything until they respond.</p>
          </div>
        )}

        {me.status === "changes_requested" && (
          <div className="card-flat p-5 border-l-4 border-l-orange-500 bg-orange-50/50" data-testid="vendor-changes-banner">
            <div className="font-display text-base text-slate-900">Studio requested changes</div>
            <p className="text-xs text-slate-600 mt-1">Please update the highlighted fields and re-submit.</p>
          </div>
        )}

        {/* Company */}
        <section className="card-flat p-5" data-testid="vendor-section-company">
          <h2 className="font-display font-bold text-sm text-slate-900 mb-3">Company details</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field k="company" label="Legal company name *" form={form} set={set} disabled={!editable} required />
            <Field k="name" label="Primary contact" form={form} set={set} disabled={!editable} />
            <Field k="phone" label="Phone" form={form} set={set} disabled={!editable} />
            <Field k="alt_mobile" label="Alt mobile" form={form} set={set} disabled={!editable} />
            <Field k="website" label="Website" form={form} set={set} disabled={!editable} />
            <Field k="category" label="Category" form={form} set={set} disabled={!editable} />
            <Field k="specialization" label="Specialization" form={form} set={set} disabled={!editable} full />
          </div>
        </section>

        {/* Address */}
        <section className="card-flat p-5" data-testid="vendor-section-address">
          <h2 className="font-display font-bold text-sm text-slate-900 mb-3">Address</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field k="address" label="Address *" form={form} set={set} disabled={!editable} textarea full required />
            <Field k="city" label="City" form={form} set={set} disabled={!editable} />
            <Field k="state" label="State" form={form} set={set} disabled={!editable} />
            <Field k="pin" label="PIN" form={form} set={set} disabled={!editable} />
            <Field k="country" label="Country" form={form} set={set} disabled={!editable} />
          </div>
        </section>

        {/* Tax + bank */}
        <section className="card-flat p-5" data-testid="vendor-section-tax">
          <h2 className="font-display font-bold text-sm text-slate-900 mb-3">Tax & bank</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field k="gstin" label="GSTIN / Tax ID *" form={form} set={set} disabled={!editable} required />
            <Field k="pan" label="PAN" form={form} set={set} disabled={!editable} />
            <Field k="payment_terms" label="Payment terms *" form={form} set={set} disabled={!editable} required />
            <Field k="bank_name" label="Bank name" form={form} set={set} disabled={!editable} />
            <Field k="bank_account" label="Account no." form={form} set={set} disabled={!editable} />
            <Field k="bank_ifsc" label="IFSC" form={form} set={set} disabled={!editable} />
            <Field k="bank_swift" label="SWIFT / IBAN (intl)" form={form} set={set} disabled={!editable} />
          </div>
        </section>

        {/* Capabilities */}
        <section className="card-flat p-5" data-testid="vendor-section-capabilities">
          <h2 className="font-display font-bold text-sm text-slate-900 mb-3">Capabilities & certifications</h2>
          <div>
            <Label className="label-overline">Capabilities (comma-separated)</Label>
            <Textarea rows={2} className="rounded-sm mt-1 resize-none"
              value={(form.capabilities || []).join(", ")}
              onChange={(e) => set("capabilities", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              disabled={!editable} data-testid="vendor-form-capabilities" placeholder="e.g. CNC machining, 3D printing, sheet metal" />
          </div>
          <div className="mt-3">
            <Label className="label-overline">Certifications (comma-separated)</Label>
            <Textarea rows={2} className="rounded-sm mt-1 resize-none"
              value={(form.certifications || []).join(", ")}
              onChange={(e) => set("certifications", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
              disabled={!editable} data-testid="vendor-form-certifications" placeholder="e.g. ISO 9001, IATF 16949" />
          </div>
        </section>

        {/* NDA */}
        <section className="card-flat p-5" data-testid="vendor-section-nda">
          <h2 className="font-display font-bold text-sm text-slate-900 mb-1">NDA</h2>
          <p className="text-[11px] text-slate-500 mb-3">By signing you confirm you&apos;ll keep any project specs, drawings and BOMs shared with you confidential.</p>
          {me.nda_signed_at ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700" data-testid="vendor-nda-signed">
              <Check size={14} /> Signed on {formatDateTime(me.nda_signed_at)}
            </div>
          ) : editable ? (
            <>
              <Button onClick={() => setSigOpen(true)} className="rounded-sm bg-slate-900 hover:bg-slate-700 text-white" data-testid="vendor-open-sig"><FileSignature size={13} className="mr-1.5" />Sign NDA</Button>
              <SignaturePad open={sigOpen} onClose={() => setSigOpen(false)} onSign={onSign} signerNameDefault={form.name || form.primary_contact} signing={signing} />
            </>
          ) : (
            <div className="text-[11px] text-slate-400">NDA pending — re-open editing to sign.</div>
          )}
        </section>

        {/* Footer actions */}
        {editable && (
          <div className="flex items-center justify-between gap-3 pt-2">
            <Button onClick={() => save(false)} variant="outline" disabled={saving} className="rounded-sm" data-testid="vendor-save-btn">
              {saving ? <Loader2 size={13} className="animate-spin mr-1.5" /> : <Save size={13} className="mr-1.5" />}Save & resume later
            </Button>
            <div className="flex flex-col items-end gap-1">
              {(missing.length > 0 || !me.nda_signed_at) && (
                <div className="text-[10px] text-slate-500" data-testid="vendor-submit-missing">
                  Missing: {[...missing, !me.nda_signed_at && "NDA signature"].filter(Boolean).join(", ")}
                </div>
              )}
              <Button onClick={submit} disabled={saving || missing.length > 0 || !me.nda_signed_at} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="vendor-submit-btn">
                <Send size={13} className="mr-1.5" /> Submit for review
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ k, label, form, set, disabled, textarea, full, required }) {
  const isMissing = required && !form[k];
  const cls = `rounded-sm mt-1 ${isMissing ? "border-orange-300" : ""}`;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <Label className="label-overline">{label}</Label>
      {textarea ? (
        <Textarea rows={2} className={`${cls} resize-none`} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={disabled} data-testid={`vendor-form-${k}`} />
      ) : (
        <Input className={cls} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} disabled={disabled} data-testid={`vendor-form-${k}`} />
      )}
    </div>
  );
}
